import React, { useState, useEffect, useCallback } from "react";
import { getWhatsappAdminReadings, deleteWhatsappReading } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Search, X, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const PAGE_SIZE = 50;

const today = () => format(new Date(), "yyyy-MM-dd");

export default function WhatsappAdminPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";

  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [medidorFilter, setMedidorFilter] = useState("");
  const [medidorInput, setMedidorInput] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete(row) {
    if (!window.confirm(`¿Eliminar el registro del medidor "${row.medidor}"? También se borrará la foto de Cloudinary.`)) return;
    setDeleting(true);
    try {
      await deleteWhatsappReading(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((prev) => prev - 1);
      setSelected(null);
    } catch (err) {
      window.alert(err.message || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await getWhatsappAdminReadings({
        from,
        to,
        medidor: medidorFilter || undefined,
        page: p,
        limit: PAGE_SIZE,
      });
      setRows(res.data);
      setTotal(res.total);
      setPage(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, medidorFilter]);

  useEffect(() => {
    load(1);
  }, [load]);

  function applyMedidor() {
    setMedidorFilter(medidorInput.trim());
  }

  function clearMedidor() {
    setMedidorInput("");
    setMedidorFilter("");
  }

  return (
    <div>
      <h1 style={s.title}>WhatsApp — Registros</h1>

      {/* Filtros */}
      <div style={s.filterBar}>
        <div style={s.filterGroup}>
          <label style={s.filterLabel}>Desde</label>
          <input
            type="date"
            style={s.dateInput}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div style={s.filterGroup}>
          <label style={s.filterLabel}>Hasta</label>
          <input
            type="date"
            style={s.dateInput}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div style={s.filterGroup}>
          <label style={s.filterLabel}>Medidor</label>
          <div style={s.searchRow}>
            <input
              style={s.searchInput}
              placeholder="Buscar medidor..."
              value={medidorInput}
              onChange={(e) => setMedidorInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyMedidor()}
            />
            {medidorFilter ? (
              <button style={s.iconBtn} onClick={clearMedidor} title="Limpiar">
                <X size={14} />
              </button>
            ) : (
              <button style={s.iconBtn} onClick={applyMedidor} title="Buscar">
                <Search size={14} />
              </button>
            )}
          </div>
        </div>
        <div style={s.filterTotal}>
          <span style={s.totalBadge}>{total.toLocaleString("es-AR")}</span>
          <span style={s.totalLabel}>registros</span>
        </div>
      </div>

      {/* Tabla */}
      <div style={s.card}>
        {error && <div style={s.errorBox}>{error}</div>}

        {loading ? (
          <div style={s.empty}>Cargando...</div>
        ) : rows.length === 0 ? (
          <div style={s.empty}>No hay registros para los filtros seleccionados</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[
                    "Medidor",
                    "Teléfono",
                    "Nombre",
                    "Lectura",
                    "Turno",
                    "Naturgy",
                    "Observación",
                    "Usuario",
                    "Fecha / Hora",
                  ].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    style={s.trClickable}
                    onClick={() => setSelected(row)}
                  >
                    <td style={s.td}><strong>{row.medidor}</strong></td>
                    <td style={s.td}>{row.telefono}</td>
                    <td style={s.td}>{row.nombre_contacto || "—"}</td>
                    <td style={s.td}>{row.lectura || "—"}</td>
                    <td style={{ ...s.td, textAlign: "center" }}>{row.turno ?? "—"}</td>
                    <td style={s.td}>
                      <span style={row.enviado_naturgy ? s.chipYes : s.chipNo}>
                        {row.enviado_naturgy ? "Sí" : "No"}
                      </span>
                    </td>
                    <td style={{ ...s.td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.observacion || "—"}
                    </td>
                    <td style={s.td}>{row.usuario_name || "—"}</td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      {format(new Date(row.created_at), "dd/MM/yy HH:mm", { locale: es })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div style={s.pagination}>
            <button
              style={{ ...s.pageBtn, opacity: page <= 1 ? 0.4 : 1 }}
              disabled={page <= 1}
              onClick={() => load(page - 1)}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={s.pageInfo}>
              Página {page} de {totalPages}
            </span>
            <button
              style={{ ...s.pageBtn, opacity: page >= totalPages ? 0.4 : 1 }}
              disabled={page >= totalPages}
              onClick={() => load(page + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{ ...s.overlay, zIndex: 10000, background: "rgba(0,0,0,0.88)", cursor: "zoom-out" }}
          onClick={() => setLightbox(null)}
        >
          <button
            style={s.lightboxClose}
            onClick={() => setLightbox(null)}
          >×</button>
          <img
            src={lightbox}
            alt=""
            style={s.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Modal detalle */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>Medidor {selected.medidor}</span>
              <button style={s.modalClose} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              {isAdminPlus && (
                <div style={s.deleteWrap}>
                  <button
                    style={{ ...s.deleteBtn, ...(deleting ? s.deleteBtnDisabled : {}) }}
                    onClick={() => handleDelete(selected)}
                    disabled={deleting}
                  >
                    <Trash2 size={14} />
                    {deleting ? "Eliminando..." : "Eliminar registro"}
                  </button>
                </div>
              )}
              {selected.photo_url ? (
                <img
                  src={selected.photo_url}
                  alt=""
                  style={s.modalImg}
                  onClick={() => setLightbox(selected.photo_url)}
                  title="Click para agrandar"
                />
              ) : (
                <div style={s.noPhoto}>Sin foto</div>
              )}
              <div style={s.modalGrid}>
                <DetailRow label="Teléfono" value={selected.telefono} />
                <DetailRow label="Nombre" value={selected.nombre_contacto} />
                <DetailRow label="Lectura" value={selected.lectura} />
                <DetailRow label="Turno" value={selected.turno} />
                <DetailRow
                  label="Enviado a Naturgy"
                  value={
                    <span style={selected.enviado_naturgy ? s.chipYes : s.chipNo}>
                      {selected.enviado_naturgy ? "Sí" : "No"}
                    </span>
                  }
                />
                <DetailRow label="Observación" value={selected.observacion} />
                <DetailRow label="Usuario" value={selected.usuario_name} />
                <DetailRow
                  label="Fecha / Hora"
                  value={format(new Date(selected.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      <span style={s.detailValue}>{value ?? "—"}</span>
    </div>
  );
}

const s = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "var(--text)",
    margin: "0 0 24px",
  },

  filterBar: {
    display: "flex",
    alignItems: "flex-end",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  filterGroup: { display: "flex", flexDirection: "column", gap: 5 },
  filterLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  dateInput: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    fontSize: 13,
    color: "var(--text)",
    background: "var(--bg-card)",
    fontFamily: "inherit",
    outline: "none",
  },
  searchRow: { display: "flex", gap: 6 },
  searchInput: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    fontSize: 13,
    color: "var(--text)",
    background: "var(--bg-card)",
    fontFamily: "inherit",
    outline: "none",
    width: 200,
  },
  iconBtn: {
    padding: "9px 11px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  filterTotal: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
    paddingBottom: 2,
  },
  totalBadge: {
    background: "var(--primary-bg)",
    color: "#D4620D",
    borderRadius: 8,
    padding: "4px 12px",
    fontSize: 15,
    fontWeight: 700,
  },
  totalLabel: { fontSize: 13, color: "var(--text-2)", fontWeight: 600 },

  card: {
    background: "var(--bg-card)",
    borderRadius: 14,
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },

  errorBox: {
    background: "var(--error-bg)",
    color: "var(--error-text)",
    borderRadius: 9,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },

  empty: {
    textAlign: "center",
    padding: "40px 0",
    color: "var(--text-2)",
    fontSize: 14,
  },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "11px 14px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--text-2)",
    borderBottom: "1px solid var(--border)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
    background: "var(--bg-card)",
  },
  tr: { borderBottom: "1px solid var(--border)" },
  trClickable: {
    borderBottom: "1px solid var(--border)",
    cursor: "pointer",
    transition: "background .12s",
  },
  td: { padding: "11px 14px", color: "var(--text)", verticalAlign: "middle" },

  chipYes: {
    background: "var(--primary-bg)",
    color: "#D4620D",
    borderRadius: 6,
    padding: "2px 9px",
    fontSize: 11,
    fontWeight: 700,
  },
  chipNo: {
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    borderRadius: 6,
    padding: "2px 9px",
    fontSize: 11,
    fontWeight: 700,
  },

  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid var(--border)",
  },
  pageBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    cursor: "pointer",
    transition: "opacity .15s",
  },
  pageInfo: { fontSize: 13, fontWeight: 600, color: "var(--text-2)" },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 24,
  },
  modal: {
    background: "var(--bg-card)",
    borderRadius: 16,
    width: "100%",
    maxWidth: 540,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px 16px",
    borderBottom: "1px solid var(--border)",
  },
  modalTitle: { fontSize: 15, fontWeight: 700, color: "var(--text)" },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: 22,
    color: "var(--text-2)",
    cursor: "pointer",
    lineHeight: 1,
    padding: "0 4px",
  },
  modalBody: { padding: 20 },
  modalImg: {
    width: "100%",
    borderRadius: 10,
    display: "block",
    objectFit: "contain",
    maxHeight: 320,
    marginBottom: 20,
    background: "var(--bg-subtle)",
    cursor: "zoom-in",
  },
  lightboxClose: {
    position: "absolute",
    top: 18,
    right: 18,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    borderRadius: "50%",
    width: 38,
    height: 38,
    color: "#fff",
    fontSize: 22,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lightboxImg: {
    maxWidth: "92vw",
    maxHeight: "92vh",
    borderRadius: 10,
    boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
  },
  noPhoto: {
    width: "100%",
    height: 120,
    borderRadius: 10,
    background: "var(--bg-subtle)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-2)",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 20,
  },
  modalGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid var(--border)",
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  detailValue: { fontSize: 13, fontWeight: 600, color: "var(--text)" },

  deleteWrap: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  },
  deleteBtn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 16px",
    borderRadius: 9,
    border: "1px solid var(--error-border)",
    background: "var(--error-bg)",
    color: "var(--error-text)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  deleteBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};
